import config from 'config';

/**
 * Fetch browse facets with options for a specific page
 * We need to fetch facets with options so that we can handle edge cases for
 * facets that were applied via URL params but are not included in the default
 * facets response from CIO.
 *
 * @param {number} page - Page number
 * @param {Object} cioClient - CIO client
 * @returns {Promise<Object>} - CIO browse facets with options for the page
 */
const getBrowseFacetsWithFacetOptionsPerPage = async ({ page, cioClient }) => {
  const hiddenFacets = config.get('public.brand.constructor.hiddenFacets');

  const params = new URLSearchParams({
    key: cioClient?.options?.apiKey,
    page,
    num_results_per_page: 10,
  });
  hiddenFacets.forEach((facet, index) => {
    params.append(`fmt_options[hidden_facets]`, facet);
  });

  const baseUrl = cioClient?.options?.serviceUrl;
  const url = `${baseUrl}/browse/facets_with_options?${params.toString()}`;

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((response) => response.json());
};

/**
 * Get all registered browse facets from CIO
 * We use this to whitelist selected URL filters in the grid.
 * If a filter is not in the CIO facets, we ignore it.
 *
 * @param {Object} cioClient - CIO client
 * @returns {Promise<Object>} - CIO browse facets
 */
const getAllCioBrowseFacets = async ({
  cioClient,
  fetchWithFacetOptions = true,
}) => {
  if (!cioClient) {
    throw new Error('CIO client is required');
  }

  /**
   * Estimated number of pages and results per page for fetching facets with options
   * This is to optimize speed in running multiple requests in parallel
   * while minimizing the number of requests made. If we underestimate,
   * we will make additional requests to fetch remaining pages.
   *
   * If we notice that the number of facets increases significantly,
   * we can increase the estimatedNumPages value, so that we can maximize
   * the chances of fetching all facets in the first batch of requests.
   */
  const estimatedNumPages = 4;
  const resultsPerPage = 10;

  if (fetchWithFacetOptions) {
    return Promise.all(
      Array.from({ length: estimatedNumPages }, (_, i) =>
        getBrowseFacetsWithFacetOptionsPerPage({ page: i + 1, cioClient })
      )
    ).then((pages) => {
      const totalNumResults = pages[0]?.response?.total_num_results || 0;
      const totalPages = Math.ceil(totalNumResults / resultsPerPage);

      // If we have fetched all pages, return the results
      if (totalPages <= estimatedNumPages) {
        return {
          response: {
            facets: pages.flatMap((page) => page?.response?.facets),
            total_num_results: totalNumResults,
          },
        };
      }

      // If we underestimated the number of pages, fetch the remaining pages
      return Promise.all(
        Array.from({ length: totalPages - estimatedNumPages }, (_, i) =>
          getBrowseFacetsWithFacetOptionsPerPage({
            page: i + 1 + estimatedNumPages,
            cioClient,
          })
        )
      ).then((remainingPages) => {
        return {
          response: {
            facets: [
              ...pages.flatMap((page) => page?.response?.facets),
              ...remainingPages.flatMap((page) => page?.response?.facets),
            ],
            total_num_results: totalNumResults,
          },
        };
      });
    });
  }

  return cioClient.browse.getBrowseFacets({
    page: 1,
    resultsPerPage: 200,
  });
};

export default getAllCioBrowseFacets;
